import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Layers, Plus, Sparkles, Loader2, ChevronDown, ChevronUp, Trash2, Pencil, CheckCircle2, Circle,
  ArrowUp, ArrowDown, Clock3, CalendarClock, Wand2, ListChecks, Bot, MessagesSquare, Activity,
  AlertTriangle, Timer, MessageSquareHeart, CalendarCheck, ListPlus,
} from 'lucide-react';

const fmtMin = (m) => {
  m = Math.round(m || 0);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const dueLabel = (d) => {
  if (!d) return null;
  const today = todayISO();
  if (d < today) return { text: 'Overdue', tone: 'hsl(var(--destructive))' };
  if (d === today) return { text: 'Today', tone: 'hsl(var(--primary))' };
  return { text: new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), tone: 'hsl(var(--muted-foreground))' };
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [upcoming, setUpcoming] = useState({ tasks: [], projects: [] });
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);
  const [newArea, setNewArea] = useState('');
  const [projDialog, setProjDialog] = useState({ open: false, areaId: null });
  const [projForm, setProjForm] = useState({ name: '', due_date: '' });
  const [assist, setAssist] = useState({ open: false, area: null, loading: false, text: '', agent: null });
  const [dash, setDash] = useState({ open: false, area: null, loading: false, data: null });
  const [review, setReview] = useState({ open: false, area: null, loading: false, text: '', agent: null });

  const load = useCallback(async () => {
    const [a, p, t, u, ag] = await Promise.all([
      api.get('/areas'), api.get('/projects'), api.get('/tasks'), api.get('/life/upcoming'), api.get('/agents'),
    ]);
    setAreas(a.data); setProjects(p.data); setTasks(t.data); setUpcoming(u.data); setAgents(ag.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const assignAgent = async (area, agentId) => {
    await api.put(`/areas/${area.area_id}`, { name: area.name, agent_id: agentId });
    setAreas((list) => list.map((a) => a.area_id === area.area_id
      ? { ...a, agent_id: agentId || null, agent: agentId ? (agents.find((g) => g.agent_id === agentId) || a.agent) : null }
      : a));
    toast.success(agentId ? 'Agent assigned to this area.' : 'Agent unassigned.');
  };

  const setCadence = async (area, cadence) => {
    await api.put(`/areas/${area.area_id}`, { name: area.name, review_cadence: cadence });
    setAreas((list) => list.map((a) => a.area_id === area.area_id ? { ...a, review_cadence: cadence } : a));
    toast.success(cadence === 'off' ? 'Weekly review email turned off.' : `Review email set to ${cadence}.`);
  };

  const chatWithAreaAgent = async (area) => {
    if (!area.agent_id) return;
    setBusy('chat-' + area.area_id);
    try {
      const res = await api.post('/chats', { type: 'direct', agent_ids: [area.agent_id], title: `${area.name} help` });
      navigate(`/app/chats/${res.data.chat_id}`);
    } catch (e) { toast.error('Could not start chat'); }
    finally { setBusy(null); }
  };

  const askAssist = async (area) => {
    if (!area.agent_id) { toast.error('Assign an agent to this area first.'); return; }
    setAssist({ open: true, area, loading: true, text: '', agent: area.agent });
    try {
      const res = await api.post(`/areas/${area.area_id}/assist`);
      setAssist({ open: true, area, loading: false, text: res.data.suggestions, agent: res.data.agent });
    } catch (e) {
      setAssist({ open: true, area, loading: false, text: e?.response?.data?.detail || 'Could not get suggestions.', agent: area.agent });
    }
  };

  const openDashboard = async (area) => {
    setDash({ open: true, area, loading: true, data: null });
    try {
      const res = await api.get(`/areas/${area.area_id}/dashboard`);
      setDash({ open: true, area, loading: false, data: res.data });
    } catch (e) {
      setDash({ open: true, area, loading: false, data: null });
      toast.error('Could not load dashboard');
    }
  };

  const openReview = async (area) => {
    setReview({ open: true, area, loading: true, text: '', agent: null });
    try {
      const res = await api.post(`/areas/${area.area_id}/weekly-review`);
      setReview({ open: true, area, loading: false, text: res.data.review, agent: res.data.agent });
    } catch (e) {
      setReview({ open: true, area, loading: false, text: e?.response?.data?.detail || 'Could not generate the review.', agent: null });
    }
  };

  const addAssistAsTasks = async () => {
    const lines = (assist.text || '').split('\n').map((l) => l.trim())
      .filter((l) => /^[-*\u2022]\s+/.test(l))
      .map((l) => l.replace(/^[-*\u2022]\s+/, '').trim())
      .filter(Boolean);
    if (lines.length === 0) { toast.error('No bullet suggestions to turn into tasks.'); return; }
    setBusy('assist-tasks');
    try {
      await Promise.all(lines.map((title) => api.post('/tasks', { title: title.slice(0, 180), status: 'todo', area_id: assist.area.area_id })));
      toast.success(`Added ${lines.length} task${lines.length === 1 ? '' : 's'} to ${assist.area.name}.`);
      setAssist((s) => ({ ...s, open: false }));
      load();
    } catch (e) { toast.error('Could not add tasks'); }
    finally { setBusy(null); }
  };


  const projectTasks = (pid) => tasks.filter((t) => t.project_id === pid && t.status !== 'archived' && !t.parent_id)
    .sort((x, y) => (x.order ?? 0) - (y.order ?? 0));

  const addArea = async () => {
    if (!newArea.trim()) return;
    await api.post('/areas', { name: newArea.trim() });
    setNewArea(''); toast.success('Area added'); load();
  };
  const renameArea = async (a) => {
    const name = window.prompt('Rename area', a.name);
    if (name && name.trim()) { await api.put(`/areas/${a.area_id}`, { name: name.trim() }); load(); }
  };
  const delArea = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? Its projects/tasks stay but become unassigned.`)) return;
    await api.delete(`/areas/${a.area_id}`); toast.success('Area deleted'); load();
  };

  const createProject = async () => {
    if (!projForm.name.trim()) return;
    await api.post('/projects', { name: projForm.name.trim(), area_id: projDialog.areaId, due_date: projForm.due_date || null });
    setProjDialog({ open: false, areaId: null }); setProjForm({ name: '', due_date: '' });
    toast.success('Project created'); load();
  };
  const delProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    await api.delete(`/projects/${p.project_id}`); load();
  };

  const breakdown = async (p) => {
    setBusy('bd-' + p.project_id);
    try { const r = await api.post(`/projects/${p.project_id}/breakdown`); toast.success(`Added ${r.data.created.length} tasks`); setExpanded((e) => ({ ...e, [p.project_id]: true })); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Breakdown failed'); }
    finally { setBusy(null); }
  };
  const prioritize = async (p) => {
    setBusy('pr-' + p.project_id);
    try { const r = await api.post('/tasks/prioritize', { project_id: p.project_id }); const n = r.data.order?.length || 0; toast.success(n ? `Reordered ${n} tasks by what to do first` : 'Nothing to prioritize yet'); load(); }
    catch (e) { toast.error('Could not prioritize'); }
    finally { setBusy(null); }
  };
  const estimate = async (t) => {
    setBusy('es-' + t.task_id);
    try { const r = await api.post(`/tasks/${t.task_id}/estimate`); toast.success(`Estimated ${fmtMin(r.data.estimate_min)}`); load(); }
    catch (e) { toast.error('Could not estimate'); }
    finally { setBusy(null); }
  };
  const toggleDone = async (t) => {
    await api.put(`/tasks/${t.task_id}`, { title: t.title, status: t.status === 'done' ? 'todo' : 'done' });
    load();
  };
  const move = async (pid, idx, dir) => {
    const list = projectTasks(pid);
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((t) => t.task_id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setTasks((prev) => prev.map((t) => { const ni = ids.indexOf(t.task_id); return ni >= 0 ? { ...t, order: ni } : t; }));
    await api.post('/tasks/reorder', { task_ids: ids });
  };
  const planToday = async () => {
    setBusy('plan');
    try { await api.post('/plan/ai', { date: todayISO(), respect_now: true }); toast.success('Planned into Today'); navigate('/app/today'); }
    catch (e) { toast.error('Could not plan'); }
    finally { setBusy(null); }
  };

  const TaskRow = ({ t, idx, list, pid }) => (
    <div className="flex items-center gap-2 py-1.5 pl-2 pr-1 rounded-lg hover:bg-[hsl(var(--surface-2))]" data-testid={`task-row-${t.task_id}`}>
      <button onClick={() => toggleDone(t)} data-testid={`task-done-${t.task_id}`}>
        {t.status === 'done' ? <CheckCircle2 className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} /> : <Circle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}
      </button>
      <span className={`text-sm flex-1 ${t.status === 'done' ? 'line-through opacity-50' : ''}`}>{t.title}</span>
      <Badge variant="outline" className="text-[10px] gap-1"><Clock3 className="h-3 w-3" />{fmtMin(t.estimate_min)}</Badge>
      <button title="AI estimate" onClick={() => estimate(t)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`estimate-${t.task_id}`}>
        {busy === 'es-' + t.task_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
      </button>
      {pid && <>
        <button onClick={() => move(pid, idx, -1)} disabled={idx === 0} className="disabled:opacity-30 text-[hsl(var(--muted-foreground))]" data-testid={`up-${t.task_id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
        <button onClick={() => move(pid, idx, 1)} disabled={idx === list.length - 1} className="disabled:opacity-30 text-[hsl(var(--muted-foreground))]" data-testid={`down-${t.task_id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      </>}
    </div>
  );

  const ProjectCard = ({ p }) => {
    const pts = projectTasks(p.project_id);
    const r = p.rollup || { task_total: 0, task_done: 0, remaining_min: 0 };
    const d = dueLabel(p.due_date);
    const open = expanded[p.project_id];
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] bg-card p-3" data-testid={`project-card-${p.project_id}`}>
        <div className="flex items-start gap-2">
          <button onClick={() => setExpanded((e) => ({ ...e, [p.project_id]: !open }))} className="mt-0.5 text-[hsl(var(--muted-foreground))]" data-testid={`toggle-project-${p.project_id}`}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-600 text-sm">{p.name}</p>
              {d && <Badge variant="secondary" className="text-[10px] gap-1" style={{ color: d.tone }}><CalendarClock className="h-3 w-3" />{d.text}</Badge>}
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{r.task_done}/{r.task_total} done · {fmtMin(r.remaining_min)} left</p>
          </div>
          <button onClick={() => delProject(p)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`del-project-${p.project_id}`}><Trash2 className="h-4 w-4" /></button>
        </div>

        {open && (
          <div className="mt-2 pl-6">
            {pts.length === 0 ? <p className="text-xs text-[hsl(var(--muted-foreground))] py-1">No tasks yet — let AI break it down.</p>
              : pts.map((t, i) => <TaskRow key={t.task_id} t={t} idx={i} list={pts} pid={p.project_id} />)}
            <div className="flex flex-wrap gap-2 mt-2">
              <Button size="sm" variant="secondary" className="rounded-lg h-8 text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => breakdown(p)} disabled={busy === 'bd-' + p.project_id} data-testid={`breakdown-${p.project_id}`}>
                {busy === 'bd-' + p.project_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Break into tasks
              </Button>
              <Button size="sm" variant="secondary" className="rounded-lg h-8 text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => prioritize(p)} disabled={busy === 'pr-' + p.project_id} data-testid={`prioritize-${p.project_id}`}>
                {busy === 'pr-' + p.project_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />} What to do first
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))]">
        <div><h1 className="font-display text-xl font-700 flex items-center gap-2"><Layers className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> Projects</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Organise your life by area, see what's due, and let AI help you plan.</p></div>
        <Button className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={planToday} disabled={busy === 'plan'} data-testid="plan-today-button">
          {busy === 'plan' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Plan my day
        </Button>
      </div>

      <Tabs defaultValue="area" className="flex-1 flex flex-col min-h-0">
        <div className="px-6 pt-3">
          <TabsList>
            <TabsTrigger value="area" data-testid="tab-by-area">By Area</TabsTrigger>
            <TabsTrigger value="due" data-testid="tab-by-due">By Due Date</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="area" className="flex-1 min-h-0 m-0">
          <ScrollArea className="h-full">
            <div className="p-6 space-y-6">
              {areas.map((a) => {
                const aps = projects.filter((p) => p.area_id === a.area_id);
                return (
                  <div key={a.area_id} data-testid={`area-${a.area_id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="h-8 w-8 rounded-lg grid place-items-center text-lg" style={{ background: (a.color || '#0e9488') + '22' }}>{a.icon}</span>
                      <h2 className="font-display font-600">{a.name}</h2>
                      <button onClick={() => renameArea(a)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid={`edit-area-${a.area_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => delArea(a)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>

                      <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                        {/* Assigned agent picker */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-1.5 rounded-lg h-8 px-2.5 text-xs border border-[hsl(var(--border))] bg-card hover:border-[hsl(var(--primary)/0.4)] transition-colors" data-testid={`area-agent-trigger-${a.area_id}`}>
                              {a.agent ? (
                                <><span className="text-sm leading-none">{a.agent.avatar || '\u{1F916}'}</span><span className="font-500 max-w-[120px] truncate">{a.agent.name}</span></>
                              ) : (
                                <><Bot className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" /><span className="text-[hsl(var(--muted-foreground))]">Assign agent</span></>
                              )}
                              <ChevronDown className="h-3 w-3 opacity-60" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-auto">
                            <DropdownMenuLabel>Helper for {a.name}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {agents.length === 0 && <div className="px-2 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">No agents yet. Create one in Agents.</div>}
                            {agents.map((g) => (
                              <DropdownMenuItem key={g.agent_id} onClick={() => assignAgent(a, g.agent_id)} data-testid={`assign-agent-${a.area_id}-${g.agent_id}`}>
                                <span className="mr-2">{g.avatar || '\u{1F916}'}</span>
                                <span className="truncate flex-1">{g.name}</span>
                                {a.agent_id === g.agent_id && <CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'hsl(var(--success))' }} />}
                              </DropdownMenuItem>
                            ))}
                            {a.agent_id && <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => assignAgent(a, '')} className="text-[hsl(var(--destructive))]" data-testid={`unassign-agent-${a.area_id}`}>Remove agent</DropdownMenuItem>
                            </>}
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]"><CalendarCheck className="h-3 w-3" /> Review email</DropdownMenuLabel>
                            {['weekly', 'monthly', 'off'].map((c) => (
                              <DropdownMenuItem key={c} onClick={() => setCadence(a, c)} data-testid={`cadence-${a.area_id}-${c}`}>
                                <span className="flex-1 capitalize">{c === 'off' ? 'Off' : c}</span>
                                {(a.review_cadence || 'weekly') === c && <CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'hsl(var(--success))' }} />}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>

                        {a.agent_id && (
                          <>
                            <Button size="sm" variant="secondary" className="rounded-lg h-8 text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => chatWithAreaAgent(a)} disabled={busy === 'chat-' + a.area_id} data-testid={`area-chat-${a.area_id}`}>
                              {busy === 'chat-' + a.area_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessagesSquare className="h-3.5 w-3.5" />} Chat
                            </Button>
                            <Button size="sm" variant="secondary" className="rounded-lg h-8 text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => askAssist(a)} data-testid={`area-assist-${a.area_id}`}>
                              <Sparkles className="h-3.5 w-3.5" /> Ask for help
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="secondary" className="rounded-lg h-8 text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => openReview(a)} data-testid={`area-review-${a.area_id}`}>
                          <CalendarCheck className="h-3.5 w-3.5" /> Review
                        </Button>
                        <Button size="sm" variant="secondary" className="rounded-lg h-8 text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => openDashboard(a)} data-testid={`area-dashboard-${a.area_id}`}>
                          <Activity className="h-3.5 w-3.5" /> Health
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-lg h-8 text-xs gap-1" onClick={() => setProjDialog({ open: true, areaId: a.area_id })} data-testid={`add-project-${a.area_id}`}><Plus className="h-3.5 w-3.5" /> Project</Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {aps.length === 0 ? <p className="text-xs text-[hsl(var(--muted-foreground))]">No projects yet.</p> : aps.map((p) => <ProjectCard key={p.project_id} p={p} />)}
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center gap-2 pt-2 border-t border-[hsl(var(--border))]">
                <Input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="Add a new life area…" className="rounded-xl max-w-xs" data-testid="new-area-input" onKeyDown={(e) => e.key === 'Enter' && addArea()} />
                <Button variant="secondary" className="rounded-xl gap-1 border border-[hsl(var(--border))]" onClick={addArea} data-testid="add-area-button"><Plus className="h-4 w-4" /> Add area</Button>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="due" className="flex-1 min-h-0 m-0">
          <ScrollArea className="h-full">
            <div className="p-6 space-y-3 max-w-3xl" data-testid="due-view">
              {upcoming.projects.length === 0 && upcoming.tasks.length === 0 && (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Nothing with a due date yet. Add due dates to projects or tasks to see them here.</p>
              )}
              {upcoming.projects.map((p) => {
                const d = dueLabel(p.due_date);
                return (
                  <div key={p.project_id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-card p-3" data-testid={`due-project-${p.project_id}`}>
                    <Layers className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
                    <div className="flex-1"><p className="font-600 text-sm">{p.name}</p><p className="text-[11px] text-[hsl(var(--muted-foreground))]">Project · {fmtMin(p.rollup?.remaining_min)} left</p></div>
                    {d && <Badge variant="secondary" className="text-[10px] gap-1" style={{ color: d.tone }}><CalendarClock className="h-3 w-3" />{d.text}</Badge>}
                  </div>
                );
              })}
              {upcoming.tasks.map((t) => {
                const d = dueLabel(t.due_date);
                return (
                  <div key={t.task_id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-card p-3" data-testid={`due-task-${t.task_id}`}>
                    <button onClick={() => toggleDone(t)}>{t.status === 'done' ? <CheckCircle2 className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} /> : <Circle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}</button>
                    <div className="flex-1"><p className="text-sm">{t.title}</p><p className="text-[11px] text-[hsl(var(--muted-foreground))]">{fmtMin(t.estimate_min)}</p></div>
                    {d && <Badge variant="secondary" className="text-[10px] gap-1" style={{ color: d.tone }}><CalendarClock className="h-3 w-3" />{d.text}</Badge>}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      <Dialog open={projDialog.open} onOpenChange={(o) => setProjDialog({ ...projDialog, open: o })}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="project-dialog">
          <DialogHeader><DialogTitle className="font-display">New project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={projForm.name} onChange={(e) => setProjForm({ ...projForm, name: e.target.value })} placeholder="Project name" className="rounded-xl" data-testid="project-name-input" />
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))]">Due date (optional)</label>
              <Input type="date" value={projForm.due_date} onChange={(e) => setProjForm({ ...projForm, due_date: e.target.value })} className="rounded-xl" data-testid="project-due-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setProjDialog({ open: false, areaId: null })}>Cancel</Button>
            <Button className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={createProject} data-testid="save-project-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Area assist dialog */}
      <Dialog open={assist.open} onOpenChange={(o) => setAssist((s) => ({ ...s, open: o }))}>
        <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="assist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <span className="text-lg">{assist.agent?.avatar || '\u{1F916}'}</span>
              {assist.agent?.name || 'Your agent'} on {assist.area?.name}
            </DialogTitle>
            <DialogDescription>A quick focus suggestion for this area of your life.</DialogDescription>
          </DialogHeader>
          <div className="min-h-24">
            {assist.loading ? (
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Thinking about {assist.area?.name}…
              </div>
            ) : (
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-4 text-sm whitespace-pre-wrap leading-relaxed" data-testid="assist-text">{assist.text}</div>
            )}
          </div>
          <DialogFooter>
            {assist.area?.agent_id && !assist.loading && assist.text && (
              <Button variant="secondary" className="rounded-xl gap-1.5 border border-[hsl(var(--border))]" onClick={addAssistAsTasks} disabled={busy === 'assist-tasks'} data-testid="assist-add-tasks">
                {busy === 'assist-tasks' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListPlus className="h-4 w-4" />} Add as tasks
              </Button>
            )}
            {assist.area?.agent_id && !assist.loading && (
              <Button variant="secondary" className="rounded-xl gap-1.5 border border-[hsl(var(--border))]" onClick={() => { const ar = assist.area; setAssist((s) => ({ ...s, open: false })); chatWithAreaAgent(ar); }} data-testid="assist-open-chat">
                <MessagesSquare className="h-4 w-4" /> Continue in chat
              </Button>
            )}
            <Button className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={() => setAssist((s) => ({ ...s, open: false }))}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weekly review dialog */}
      <Dialog open={review.open} onOpenChange={(o) => setReview((s) => ({ ...s, open: o }))}>
        <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CalendarCheck className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> {review.area?.name} weekly review
            </DialogTitle>
            <DialogDescription>Wins from the past week and a gentle focus for the next.</DialogDescription>
          </DialogHeader>
          <div className="min-h-24">
            {review.loading ? (
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Looking back over your week…
              </div>
            ) : (
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-4 text-sm whitespace-pre-wrap leading-relaxed" data-testid="review-text">{review.text}</div>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={() => setReview((s) => ({ ...s, open: false }))}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Area dashboard dialog */}
      <Dialog open={dash.open} onOpenChange={(o) => setDash((s) => ({ ...s, open: o }))}>
        <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="dashboard-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg grid place-items-center text-base" style={{ background: (dash.area?.color || '#0e9488') + '22' }}>{dash.area?.icon}</span>
              {dash.area?.name} health
            </DialogTitle>
            <DialogDescription>What's due, what's stalled, and how much time this week needs.</DialogDescription>
          </DialogHeader>
          {dash.loading || !dash.data ? (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {[
                  ['Open tasks', dash.data.counts.open_tasks, ListChecks, 'hsl(var(--primary))'],
                  ['Time left', fmtMin(dash.data.time_left_min), Timer, 'hsl(var(--accent))'],
                  ['Due this week', dash.data.counts.due_this_week, CalendarClock, 'hsl(var(--brand-honey))'],
                ].map(([label, val, Icon, color]) => (
                  <div key={label} className="rounded-xl border border-[hsl(var(--border))] bg-card p-3 text-center">
                    <Icon className="h-4 w-4 mx-auto mb-1" style={{ color }} />
                    <p className="font-display font-700 text-lg leading-none">{val}</p>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {dash.data.counts.overdue > 0 && (
                <div className="rounded-xl border p-3" style={{ borderColor: 'hsl(var(--destructive) / 0.35)', background: 'hsl(var(--destructive) / 0.06)' }} data-testid="dash-overdue">
                  <p className="text-xs font-600 flex items-center gap-1.5 mb-1.5" style={{ color: 'hsl(var(--destructive))' }}><AlertTriangle className="h-3.5 w-3.5" /> Overdue ({dash.data.counts.overdue})</p>
                  <div className="space-y-1">
                    {dash.data.overdue.map((t) => (
                      <div key={t.task_id} className="flex items-center gap-2 text-sm"><Circle className="h-3 w-3 text-[hsl(var(--muted-foreground))]" /><span className="flex-1 truncate">{t.title}</span><Badge variant="outline" className="text-[10px]">{t.due_date}</Badge></div>
                    ))}
                  </div>
                </div>
              )}

              {dash.data.stalled_projects.length > 0 && (
                <div className="rounded-xl border border-[hsl(var(--border))] bg-card p-3" data-testid="dash-stalled">
                  <p className="text-xs font-600 text-[hsl(var(--muted-foreground))] mb-1.5">Stalled projects</p>
                  <div className="space-y-1">
                    {dash.data.stalled_projects.map((p) => (
                      <div key={p.project_id} className="flex items-center gap-2 text-sm"><Layers className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} /><span className="flex-1 truncate">{p.name}</span><Badge variant="secondary" className="text-[10px]">{p.reason}</Badge></div>
                    ))}
                  </div>
                </div>
              )}

              {dash.data.due_this_week.length > 0 && (
                <div className="rounded-xl border border-[hsl(var(--border))] bg-card p-3" data-testid="dash-due-week">
                  <p className="text-xs font-600 text-[hsl(var(--muted-foreground))] mb-1.5">Due within 7 days · {fmtMin(dash.data.week_left_min)}</p>
                  <div className="space-y-1">
                    {dash.data.due_this_week.map((t) => (
                      <div key={t.task_id} className="flex items-center gap-2 text-sm"><Circle className="h-3 w-3 text-[hsl(var(--muted-foreground))]" /><span className="flex-1 truncate">{t.title}</span><Badge variant="outline" className="text-[10px]">{t.due_date}</Badge></div>
                    ))}
                  </div>
                </div>
              )}

              {dash.data.counts.open_tasks === 0 && dash.data.counts.projects === 0 && (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">Nothing here yet. Add a project or a few tasks to this area.</p>
              )}

              {dash.area?.agent_id && (
                <Button className="w-full rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={() => { const ar = dash.area; setDash((s) => ({ ...s, open: false })); askAssist(ar); }} data-testid="dash-ask-agent">
                  <Sparkles className="h-4 w-4" /> Ask {dash.area?.agent?.name || 'your agent'} what to do first
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
